import React, { useState, useEffect } from "react";
import { Tooltip } from "../GeneralComponents/ui";
import { LoadingOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { get } from "../../Utils/WebService";
import i18n from "../../Utils/i18next";
import { getThemeProperties } from "./getThemeProperties";
import reportThumbnail from "../../images/report-thumbnail.png";
import dashboardThumbnail from "../../images/newDashboard.png";
import { useDashboardContext } from "./DashboardContext";
import { hexToRgba } from "../../Utils/Color";

const ReportThumbnail = ({ data, title, style, theme }) => {
  const { imageCache, errorCache } = useDashboardContext();
  const [resolvedImageUrl, setResolvedImageUrl] = useState(null);
  const [isFallback, setIsFallback] = useState(false);

  useEffect(() => {
    if (!data) return;
    let isMounted = true;

    const resolveAndCache = () => {
      const urlsToTry = [];

      // 1. Try Custom Thumbnail
      if (data.customThumbnail) {
        urlsToTry.push({
          url: `/resource/api/resource/${data.customThumbnail}`,
          isResource: true,
          isPrimary: true
        });
      }

      // 2. Try Standard Thumbnail (or report placeholder)
      if (data.type === "report") {
        urlsToTry.push({ url: reportThumbnail, isResource: false, isPrimary: !data.customThumbnail });
      } else if (data.thumbnail) {
        urlsToTry.push({
          url: `/resource/api/resource/${data.thumbnail}`,
          isResource: true,
          isPrimary: !data.customThumbnail
        });
      } else {
        urlsToTry.push({ url: dashboardThumbnail, isResource: false, isPrimary: true });
      }

      const tryResolve = (index) => {
        if (!isMounted) return;

        if (index >= urlsToTry.length) {
          setResolvedImageUrl("ERROR");
          setIsFallback(false);
          return;
        }

        const { url, isResource, isPrimary } = urlsToTry[index];

        if (errorCache?.current?.has(url)) {
          tryResolve(index + 1);
          return;
        }

        if (imageCache?.current?.has(url)) {
          setResolvedImageUrl(imageCache.current.get(url));
          setIsFallback(!isPrimary);
          return;
        }

        if (!isResource) {
          if (imageCache?.current) {
            imageCache.current.set(url, url);
          }
          setResolvedImageUrl(url);
          setIsFallback(!isPrimary);
          return;
        }

        const success = (response) => {
          if (!isMounted) return;
          const blob = response.data;
          const objectUrl = URL.createObjectURL(blob);
          if (imageCache?.current) {
            imageCache.current.set(url, objectUrl);
          }
          setResolvedImageUrl(objectUrl);
          setIsFallback(!isPrimary);
        };

        const error = () => {
          if (!isMounted) return;
          if (errorCache?.current) {
            errorCache.current.add(url);
          }
          tryResolve(index + 1);
        };

        get(
          url,
          success,
          error,
          false,
          false,
          undefined,
          true,
          undefined,
          false,
          "blob",
        );
      };

      tryResolve(0);
    };

    resolveAndCache();

    return () => {
      isMounted = false;
    };
  }, [data, imageCache, errorCache]);

  const cardBg = theme ? theme.backgroundColor : getThemeProperties("reportBackgroundColor") || "--primary-color";
  const cardText = theme ? theme.textColor : getThemeProperties("reportTextColor") || "#111827";

  const imageStyle = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "center top",

  };

  const renderExclamation = () => (
    <div
      style={{
        position: "absolute",
        top: 10,
        left: 10,
        zIndex: 5,
        background: "rgba(239, 68, 68, 0.9)",
        color: "#fff",
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: "bold",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
      }}
    >
      !
    </div>
  );

  return (
    <div
      className="report-image"
      style={{
        position: "relative",
        background: hexToRgba(cardText, 0.05),
        overflow: "hidden",
        ...style,
      }}
    >
      {isFallback && renderExclamation()}

      {resolvedImageUrl === "ERROR" ? (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 0,
          }}
        >
          <Tooltip title={i18n.t("LandingPage.ImageError")}>
            <ExclamationCircleOutlined
              style={{
                fontSize: "32px",
                color: hexToRgba(cardText, 0.2),
              }}
            />
          </Tooltip>
        </div>
      ) : (
        <>
          {!resolvedImageUrl && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 0,
              }}
            >
              <LoadingOutlined style={{ color: hexToRgba(cardText, 0.2) }} />
            </div>
          )}
          {resolvedImageUrl && (
            <img
              src={resolvedImageUrl}
              alt={title || "Dashboard Thumbnail"}
              style={imageStyle}
            />
          )}
        </>
      )}
    </div>
  );
};

export default React.memo(ReportThumbnail);
